/**
 * On-device Vega DSP lip sync (audio → OVR mouth cues).
 * Old Azure/POC path is unchanged; toggle with REACT_APP_VEGA_LIPSYNC.
 */
import visemeConfig from "./vega/viseme-config.json";
import VisemeEngine from "./vega/viseme-engine.js";
import { frontEnvString } from "src/services/runtimeFrontConfig";
import { Capacitor, CapacitorHttp } from "@capacitor/core";

export type VegaMouthCue = {
  start: number;
  end: number;
  value: string;
};

export type VegaTrackEvent = {
  t: number;
  viseme: string;
  intensity?: number;
  level?: number;
};

export type VegaLipSyncData = {
  mouthCues: VegaMouthCue[];
  vegaTrack?: VegaTrackEvent[];
};

export type VegaLipSyncCharacter = {
  setLipSyncData: (data: VegaLipSyncData) => void;
  setPlaybackStartTime?: (startTime: number) => void;
};

type VegaEvent = VegaTrackEvent;

const VEGA_FLAG_KEY = "REACT_APP_VEGA_LIPSYNC";
let engineBannerLogged = false;

function coerceFlag(raw: unknown, fallback: string): string {
  if (typeof raw === "boolean") return raw ? "true" : "false";
  if (typeof raw === "number") return raw ? "true" : "false";
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return fallback;
}

/** true = Vega on-device (new). false = Azure/POC lipsync_data (legacy). Default on. */
export function isVegaLipSyncEnabled(): boolean {
  const raw = frontEnvString(VEGA_FLAG_KEY, "true");
  const enabled = coerceFlag(raw, "true").toLowerCase() !== "false";
  if (!engineBannerLogged) {
    engineBannerLogged = true;
    if (enabled) {
      console.log("[LipSync] ENGINE=VEGA (on-device DSP). Azure/POC mouth cues are ignored. Set REACT_APP_VEGA_LIPSYNC=false to restore the old flow.");
    } else {
      console.log("[LipSync] ENGINE=AZURE/POC (legacy). Set REACT_APP_VEGA_LIPSYNC=true to use on-device Vega.");
    }
  }
  return enabled;
}

export function applyLegacyLipSyncOrSkip(character: VegaLipSyncCharacter | null | undefined, data: VegaLipSyncData | null | undefined, source: string): void {
  if (!character || !data) return;
  if (isVegaLipSyncEnabled()) {
    console.log(`[LipSync] VEGA on-device active — skipped Azure/POC cues (${source}, ${data.mouthCues?.length ?? 0} cues)`);
    return;
  }
  console.log(`[LipSync] AZURE/POC applying cues (${source}, ${data.mouthCues?.length ?? 0} cues)`);
  character.setLipSyncData(data);
}

function eventsToCues(events: VegaEvent[]): VegaMouthCue[] {
  const cues: VegaMouthCue[] = [];
  for (let i = 0; i < events.length; i++) {
    const start = events[i].t;
    const end = i + 1 < events.length ? events[i + 1].t : start + 0.12;
    cues.push({ start, end, value: events[i].viseme });
  }
  return cues;
}

function makeEngine(sampleRate: number, lookaheadMs?: number): VisemeEngine {
  const audio = { ...(visemeConfig as { audio: { sampleRate: number } }).audio, sampleRate };
  const timing = { ...(visemeConfig as { timing: { lookaheadMs: number } }).timing };
  if (typeof lookaheadMs === "number") timing.lookaheadMs = lookaheadMs;
  return new VisemeEngine({ ...visemeConfig, audio, timing });
}

function pushAllChunks(engine: VisemeEngine, samples: Float32Array, sampleRate: number): VegaEvent[] {
  const chunk = Math.max(1, Math.round(sampleRate * 0.04));
  const events: VegaEvent[] = [];
  for (let i = 0; i < samples.length; i += chunk) {
    events.push(...engine.push(samples.subarray(i, Math.min(i + chunk, samples.length))));
  }
  events.push(...engine.flush());
  return events;
}

function yieldToAudio(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof MessageChannel !== "undefined") {
      const ch = new MessageChannel();
      ch.port1.onmessage = () => resolve();
      ch.port2.postMessage(null);
      return;
    }
    setTimeout(resolve, 0);
  });
}

function pushRange(engine: VisemeEngine, samples: Float32Array, from: number, to: number, sampleRate: number): VegaEvent[] {
  const chunk = Math.max(1, Math.round(sampleRate * 0.04));
  const events: VegaEvent[] = [];
  for (let i = from; i < to; i += chunk) {
    events.push(...engine.push(samples.subarray(i, Math.min(i + chunk, to))));
  }
  return events;
}

/** Capacitor: same DSP, yielding so the welcome tap is not starved while the rest of the file is analyzed. */
async function pushRangeYielding(engine: VisemeEngine, samples: Float32Array, from: number, to: number, sampleRate: number): Promise<VegaEvent[]> {
  const chunk = Math.max(1, Math.round(sampleRate * 0.04));
  const yieldEvery = Math.max(chunk, Math.round(sampleRate * 0.12));
  const events: VegaEvent[] = [];
  let since = 0;
  for (let i = from; i < to; i += chunk) {
    events.push(...engine.push(samples.subarray(i, Math.min(i + chunk, to))));
    since += chunk;
    if (since >= yieldEvery) {
      since = 0;
      await yieldToAudio();
    }
  }
  return events;
}

function ensureHeadWaiter(url: string): { promise: Promise<boolean>; resolve: (ok: boolean) => void; settled: boolean } {
  let waiter = vegaHeadWaiters.get(url);
  if (!waiter) {
    let resolve!: (ok: boolean) => void;
    const promise = new Promise<boolean>((r) => {
      resolve = r;
    });
    waiter = { promise, resolve, settled: false };
    vegaHeadWaiters.set(url, waiter);
  }
  return waiter;
}

function settleHead(url: string, ok: boolean): void {
  const waiter = vegaHeadWaiters.get(url);
  if (!waiter || waiter.settled) return;
  waiter.settled = true;
  waiter.resolve(ok);
}

function publishVegaUrl(url: string, events: VegaEvent[], complete: boolean): VegaUrlAnalysis {
  const result: VegaUrlAnalysis = { events: events.slice(), mouthCues: eventsToCues(events), complete };
  vegaUrlReady.set(url, result);
  if (events.length > 0) settleHead(url, true);
  else if (complete) settleHead(url, false);
  vegaUrlListeners.get(url)?.forEach((fn) => {
    try {
      fn(complete);
    } catch {
      /* ignore */
    }
  });
  return result;
}

export class VegaLipSyncSession {
  private engine: VisemeEngine;
  private events: VegaEvent[] = [];
  private readonly label: string;
  private readonly characterRef: { current: VegaLipSyncCharacter | null };

  constructor(characterRef: { current: VegaLipSyncCharacter | null }, label: string, sampleRate = 24000, lookaheadMs?: number) {
    this.characterRef = characterRef;
    this.label = label;
    this.engine = makeEngine(sampleRate, lookaheadMs);
    isVegaLipSyncEnabled();
    console.log(`[LipSync] VEGA session started (${label})`);
  }

  push(samples: Float32Array): void {
    if (!samples?.length) return;
    this.ingest(this.engine.push(samples));
  }

  flush(): void {
    this.ingest(this.engine.flush());
  }

  reset(): void {
    this.engine.reset();
    this.events = [];
    console.log(`[LipSync] VEGA session reset (${this.label})`);
  }

  hasCues(): boolean {
    return this.events.length > 0;
  }

  private ingest(newEvents: VegaEvent[]): void {
    if (!newEvents.length) return;
    this.events.push(...newEvents);
    const mouthCues = eventsToCues(this.events);
    const last = newEvents[newEvents.length - 1];
    const character = this.characterRef.current;
    if (!character) {
      console.warn(`[LipSync] VEGA produced cues but character is not ready (${this.label})`);
      return;
    }
    character.setLipSyncData({ mouthCues, vegaTrack: this.events });
    if (mouthCues.length === newEvents.length || mouthCues.length % 20 < newEvents.length) {
      console.log(`[LipSync] VEGA on-device | source=${this.label} | cues=${mouthCues.length} | viseme=${last.viseme} @ ${last.t.toFixed(2)}s`);
    }
  }
}

type VegaUrlAnalysis = { events: VegaEvent[]; mouthCues: VegaMouthCue[]; complete: boolean };
const vegaUrlJobs = new Map<string, Promise<VegaUrlAnalysis | null>>();
const vegaUrlReady = new Map<string, VegaUrlAnalysis>();
const vegaUrlListeners = new Map<string, Set<(done: boolean) => void>>();
const vegaHeadWaiters = new Map<string, { promise: Promise<boolean>; resolve: (ok: boolean) => void; settled: boolean }>();

function isCapacitorIOS(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  } catch {
    return false;
  }
}

export function isCapacitorNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function resolveAudioUrl(url: string): string {
  if (/^(https?:|blob:|capacitor:|ionic:|file:|data:)/i.test(url)) return url;
  try {
    return new URL(url, window.location.href).href;
  } catch {
    return url;
  }
}

function isRemoteHttpUrl(url: string): boolean {
  return /^https?:/i.test(url) || /^https?:/i.test(resolveAudioUrl(url));
}

/** Bundled assets on Capacitor (iOS capacitor://, Android https://localhost). Not GCS. */
function isLocalAppHost(url: string): boolean {
  if (/^(capacitor|ionic|file|blob|data):/i.test(url)) return true;
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host === "localhost" || host === "127.0.0.1";
  } catch {
    return false;
  }
}

/** WKWebView fetch/XHR rejects `|` in GCS paths; native URLSession needs it encoded too. */
function encodeUnsafeUrlPath(url: string): string {
  const abs = /^https?:/i.test(url) ? url : resolveAudioUrl(url);
  try {
    const parsed = new URL(abs);
    parsed.pathname = parsed.pathname
      .split("/")
      .map((seg) => {
        try {
          return encodeURIComponent(decodeURIComponent(seg));
        } catch {
          return encodeURIComponent(seg);
        }
      })
      .join("/");
    return parsed.toString();
  } catch {
    return abs.replace(/\|/g, "%7C");
  }
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const payload = b64.includes(",") ? b64.slice(b64.indexOf(",") + 1) : b64;
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function httpDataToArrayBuffer(data: unknown): ArrayBuffer {
  if (data instanceof ArrayBuffer) return data;
  if (ArrayBuffer.isView(data)) {
    const out = new ArrayBuffer(data.byteLength);
    new Uint8Array(out).set(new Uint8Array(data.buffer, data.byteOffset, data.byteLength));
    return out;
  }
  if (typeof data === "string" && data.length > 0) return base64ToArrayBuffer(data);
  throw new Error(`CapacitorHttp unexpected data type ${typeof data}`);
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(label)), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/** Native GET bypasses WebView CORS (iOS capacitor:// and Android https://localhost → GCS). Do not enable global CapacitorHttp fetch patching. */
async function capacitorHttpArrayBuffer(url: string, timeoutMs = 30000): Promise<ArrayBuffer> {
  const response = await CapacitorHttp.get({
    url,
    responseType: "arraybuffer",
    connectTimeout: timeoutMs,
    readTimeout: timeoutMs,
  });
  if (response.status < 200 || response.status >= 300) {
    throw new Error(`CapacitorHttp HTTP ${response.status}`);
  }
  const buf = httpDataToArrayBuffer(response.data);
  if (!buf.byteLength) throw new Error("CapacitorHttp empty body");
  return buf;
}

function errorDetail(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  try {
    return String(err);
  } catch {
    return "unknown";
  }
}

function decodeAudioDataCompat(ctx: { decodeAudioData: Function }, copy: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const ok = (buf: AudioBuffer) => {
      if (!settled) {
        settled = true;
        resolve(buf);
      }
    };
    const fail = (err: unknown) => {
      if (!settled) {
        settled = true;
        reject(err || new Error("decodeAudioData failed"));
      }
    };
    try {
      const ret = ctx.decodeAudioData(copy, ok, fail);
      if (ret && typeof ret.then === "function") ret.then(ok, fail);
    } catch (e) {
      fail(e);
    }
  });
}

async function fetchArrayBufferWeb(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  if (!buf.byteLength) throw new Error("empty body");
  return buf;
}

/** Desktop: fetch(url). Capacitor remote GCS: native HTTP (WebView fetch is CORS-blocked). iOS local capacitor://: tap fallback. */
async function readAudioBytes(url: string): Promise<ArrayBuffer> {
  if (!isCapacitorNative()) return fetchArrayBufferWeb(url);

  const abs = /^https?:/i.test(url) || /^(capacitor|ionic):/i.test(url) ? url : resolveAudioUrl(url);
  const remote = isRemoteHttpUrl(url) && !isLocalAppHost(abs);

  // iOS + Android: native GET for GCS/CDN. Do not use this for bundled localhost assets.
  if (remote) {
    const encoded = encodeUnsafeUrlPath(url);
    console.log(`[LipSync] VEGA native GET ios=${isCapacitorIOS()} url=${encoded}`);
    return capacitorHttpArrayBuffer(encoded, 30000);
  }

  if (isCapacitorIOS()) {
    if (/^capacitor:/i.test(abs)) {
      console.log(`[LipSync] VEGA iOS native GET url=${abs}`);
      try {
        const getBytes = capacitorHttpArrayBuffer(abs, 1500);
        return await withTimeout(getBytes, 1500, "iOS local GET timeout");
      } catch (e) {
        console.warn(`[LipSync] VEGA iOS native GET local failed ${errorDetail(e)}`);
      }
    }
    throw new Error("iOS local audio bytes unavailable (use HTMLAudio tap)");
  }

  return fetchArrayBufferWeb(url);
}

/** Desktop: Offline 24 kHz (unchanged). Capacitor iOS: Offline 44.1 kHz (no user-gesture AudioContext). */
async function decodeUrlAudioBuffer(arrayBuffer: ArrayBuffer): Promise<AudioBuffer> {
  const Offline = (window as any).OfflineAudioContext || (window as any).webkitOfflineAudioContext;
  if (isCapacitorIOS()) {
    const rates = [44100, 48000];
    let lastErr: unknown;
    for (const rate of rates) {
      try {
        const ctx = new Offline(1, rate, rate);
        return await decodeAudioDataCompat(ctx, arrayBuffer.slice(0));
      } catch (e) {
        lastErr = e;
      }
    }
    throw lastErr || new Error("iOS Offline decode failed");
  }
  const ctx = new Offline(1, 1, 24000);
  return decodeAudioDataCompat(ctx, arrayBuffer.slice(0));
}

async function analyzeVegaFromAudioUrl(url: string): Promise<VegaUrlAnalysis | null> {
  const existing = vegaUrlJobs.get(url);
  if (existing) return existing;
  const ready = vegaUrlReady.get(url);
  if (ready?.complete) return ready;
  const job = (async (): Promise<VegaUrlAnalysis | null> => {
    try {
      if (isCapacitorIOS()) {
        console.log(`[LipSync] VEGA iOS analyze start url=${url}`);
      }
      const arrayBuffer = await readAudioBytes(url);
      // Offline context: decode only, never route samples to speakers.
      const audioBuffer: AudioBuffer = await decodeUrlAudioBuffer(arrayBuffer);
      const samples = audioBuffer.getChannelData(0);
      const engine = makeEngine(audioBuffer.sampleRate);
      if (!isCapacitorNative()) {
        const events = pushAllChunks(engine, samples, audioBuffer.sampleRate);
        return publishVegaUrl(url, events, true);
      }
      if (!samples.length) {
        settleHead(url, false);
        return null;
      }
      // First ~2s is synchronous so drawer-open can start audio and mouth together.
      // The rest yields (welcome/HTMLAudio stay responsive) and is published every ~2s so the track stays ahead of currentTime.
      const rate = audioBuffer.sampleRate;
      const step = Math.max(1, Math.round(rate * 2));
      const headEnd = Math.min(samples.length, step);
      let emitted = pushRange(engine, samples, 0, headEnd, rate);
      const headDone = headEnd >= samples.length;
      publishVegaUrl(url, headDone ? emitted.concat(engine.flush()) : emitted, headDone);
      if (headDone) return vegaUrlReady.get(url) ?? null;
      for (let cursor = headEnd; cursor < samples.length; cursor += step) {
        const next = Math.min(samples.length, cursor + step);
        emitted = emitted.concat(await pushRangeYielding(engine, samples, cursor, next, rate));
        const end = next >= samples.length;
        publishVegaUrl(url, end ? emitted.concat(engine.flush()) : emitted, end);
      }
      return vegaUrlReady.get(url) ?? null;
    } catch (e) {
      vegaUrlJobs.delete(url);
      settleHead(url, false);
      throw new Error(`[LipSync] VEGA URL analyze failed ios=${isCapacitorIOS()} url=${url} ${errorDetail(e)}`);
    }
  })();
  vegaUrlJobs.set(url, job);
  return job;
}

/** Capacitor drawer-open: resolves when the first ~2s of visemes exist, not when the whole file is done. */
export async function prepareVegaHeadFromAudioUrl(url: string | null | undefined): Promise<boolean> {
  if (!isVegaLipSyncEnabled() || !url) return false;
  if (vegaUrlReady.get(url)?.events.length) return true;
  const waiter = ensureHeadWaiter(url);
  if (vegaUrlReady.get(url)?.events.length) {
    settleHead(url, true);
    return true;
  }
  void analyzeVegaFromAudioUrl(url).catch((e) => {
    console.warn(`[LipSync] VEGA URL head failed ${errorDetail(e)}`);
    settleHead(url, false);
  });
  return waiter.promise;
}

/** Fires when a URL track grows (head, then full). */
export function subscribeVegaUrlUpdates(url: string, cb: (done: boolean) => void): () => void {
  let set = vegaUrlListeners.get(url);
  if (!set) {
    set = new Set();
    vegaUrlListeners.set(url, set);
  }
  set.add(cb);
  return () => set?.delete(cb);
}

/** Decode visemes only. Does not apply to Quan and does not start playback. */
export async function prepareVegaFromAudioUrl(url: string | null | undefined): Promise<boolean> {
  if (!isVegaLipSyncEnabled() || !url) return false;
  try {
    const result = await analyzeVegaFromAudioUrl(url);
    return !!result;
  } catch (e) {
    console.warn(`[LipSync] VEGA URL prepare failed ${errorDetail(e)}`);
    return false;
  }
}

/** Same handoff as welcome→funny: analyze while previous audio plays, then stopPrevious() and apply. Vega-off is a no-op. */
export async function prepareThenApplyVegaFromAudioUrl(
  url: string | null | undefined,
  character: VegaLipSyncCharacter | null | undefined,
  source: string,
  stopPrevious: () => void,
): Promise<boolean> {
  if (!isVegaLipSyncEnabled() || !url) return false;
  await prepareVegaFromAudioUrl(url);
  stopPrevious();
  if (!character) return false;
  if (tryApplyVegaFromAudioUrlSync(url, character, source)) return true;
  return applyVegaFromAudioUrlIfEnabled(url, character, source);
}

/** Background analyze so cache-open can apply visemes before play (does not start audio). */
export function prefetchVegaFromAudioUrl(url: string | null | undefined): void {
  if (!isVegaLipSyncEnabled() || !url) return;
  void analyzeVegaFromAudioUrl(url).catch((e) => {
    console.warn(`[LipSync] VEGA URL prefetch failed ${errorDetail(e)}`);
  });
}

/** Sync apply only if prefetch already finished. Never blocks. */
export function tryApplyVegaFromAudioUrlSync(url: string | null | undefined, character: VegaLipSyncCharacter | null | undefined, source: string): boolean {
  if (!isVegaLipSyncEnabled() || !url || !character) return false;
  const ready = vegaUrlReady.get(url);
  if (!ready) return false;
  character.setLipSyncData({ mouthCues: ready.mouthCues, vegaTrack: ready.events });
  console.log(`[LipSync] VEGA on-device | source=${source} | cues=${ready.mouthCues.length} | from URL (ready)`);
  return true;
}

export async function applyVegaFromAudioUrlIfEnabled(url: string | null | undefined, character: VegaLipSyncCharacter | null | undefined, source: string): Promise<boolean> {
  if (!isVegaLipSyncEnabled() || !url || !character) return false;
  try {
    const result = await analyzeVegaFromAudioUrl(url);
    if (!result) return false;
    character.setLipSyncData({ mouthCues: result.mouthCues, vegaTrack: result.events });
    console.log(`[LipSync] VEGA on-device | source=${source} | cues=${result.mouthCues.length} | from URL`);
    return true;
  } catch (e) {
    console.warn(`[LipSync] VEGA URL analysis failed (${source}); falling back if Azure cues exist ${errorDetail(e)}`);
    return false;
  }
}

const htmlAudioTaps = new WeakMap<HTMLAudioElement, () => void>();
const htmlAudioTapDelaySec = new WeakMap<HTMLAudioElement, number>();

/** Speaker delay used by the iOS HTMLAudio tap. 0 on desktop / URL / PCM paths. */
export function getVegaHtmlAudioTapDelaySec(audio: HTMLAudioElement | null | undefined): number {
  if (!audio) return 0;
  return htmlAudioTapDelaySec.get(audio) ?? 0;
}

export function isVegaHtmlAudioTap(audio: HTMLAudioElement | null | undefined): boolean {
  return !!audio && htmlAudioTaps.has(audio);
}

/** Tear down the iOS HTMLAudio tap immediately (pause does not fire ended; ScriptProcessor would repeat the last buffer). */
export function stopVegaHtmlAudioTap(audio: HTMLAudioElement | null | undefined): void {
  if (!audio) return;
  htmlAudioTaps.get(audio)?.();
}

/**
 * iOS local assets only (welcome MP3). WKWebView cannot fetch capacitor:// bytes;
 * HTMLAudio can play them, so tap PCM into Vega. Remote HTTPS is CORS-tainted here — use CapacitorHttp instead.
 */
export function tryStartVegaHtmlAudioTap(
  audio: HTMLAudioElement | null | undefined,
  character: VegaLipSyncCharacter | null | undefined,
  source: string,
  onAudible?: () => void,
): boolean {
  if (!isVegaLipSyncEnabled() || !isCapacitorIOS() || !audio || !character) return false;
  const src = audio.getAttribute("src") || audio.src || "";
  if (isRemoteHttpUrl(src)) return false;
  if (htmlAudioTaps.has(audio)) return true;

  const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!AudioCtx) return false;

  let ctx: AudioContext;
  try {
    ctx = new AudioCtx();
  } catch {
    return false;
  }

  let sourceNode: MediaElementAudioSourceNode;
  try {
    sourceNode = ctx.createMediaElementSource(audio);
  } catch (e) {
    console.warn(`[LipSync] VEGA iOS HTMLAudio tap skipped ${errorDetail(e)}`);
    return false;
  }

  // Do not mute HTMLAudio (zeros MediaElementSource on iOS). Gate Web Audio until the first viseme.
  // Speakers must not go through ScriptProcessor: iOS repeats the last callback buffer when JS is busy (league tap).
  const session = new VegaLipSyncSession({ current: character }, `${source}-ios-tap`, ctx.sampleRate || 44100, 0);
  const processor = ctx.createScriptProcessor(2048, 1, 1);
  const gate = ctx.createGain();
  const tapMute = ctx.createGain();
  gate.gain.value = 0;
  tapMute.gain.value = 0;
  let tapArmed = false;
  let audible = false;
  let dead = false;
  const releaseAudio = () => {
    if (audible || dead) return;
    audible = true;
    character.setPlaybackStartTime?.(performance.now() / 1000);
    gate.gain.setValueAtTime(1, ctx.currentTime);
    onAudible?.();
    console.log(`[LipSync] VEGA iOS tap audio released (${source}) cues=${session.hasCues()}`);
  };
  processor.onaudioprocess = (e) => {
    const output = e.outputBuffer.getChannelData(0);
    // Keep the viseme tap silent. Repeating this buffer must not reach the speakers.
    output.fill(0);
    if (dead || !tapArmed || audio.paused || audio.ended) return;
    session.push(e.inputBuffer.getChannelData(0));
    if (session.hasCues()) releaseAudio();
  };
  sourceNode.connect(gate);
  gate.connect(ctx.destination);
  sourceNode.connect(processor);
  processor.connect(tapMute);
  tapMute.connect(ctx.destination);
  htmlAudioTapDelaySec.set(audio, 0);

  const resume = () => {
    void ctx.resume();
  };
  const armTap = () => {
    tapArmed = true;
    void ctx.resume();
  };
  void ctx.resume();
  audio.addEventListener("play", armTap);
  audio.addEventListener("playing", resume);
  const releaseTimer = window.setTimeout(releaseAudio, 800);

  const cleanup = () => {
    if (dead) return;
    dead = true;
    try {
      session.flush();
    } catch {
      /* ignore */
    }
    try {
      window.clearTimeout(releaseTimer);
      gate.gain.setValueAtTime(0, ctx.currentTime);
      tapMute.gain.setValueAtTime(0, ctx.currentTime);
      processor.onaudioprocess = null;
      processor.disconnect();
      tapMute.disconnect();
      gate.disconnect();
      sourceNode.disconnect();
      void ctx.close();
    } catch {
      /* ignore */
    }
    audio.removeEventListener("play", armTap);
    audio.removeEventListener("playing", resume);
    audio.removeEventListener("ended", cleanup);
    htmlAudioTaps.delete(audio);
    htmlAudioTapDelaySec.delete(audio);
  };
  audio.addEventListener("ended", cleanup);
  htmlAudioTaps.set(audio, cleanup);
  console.log(`[LipSync] VEGA iOS HTMLAudio tap started (${source})`);
  return true;
}
